import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Loader2, X, RotateCcw, ArrowLeft, AlertTriangle } from 'lucide-react';

const API = '/api';

/**
 * Emails fitters asked H-G to send to their own golfers (backend: fitter_sends.py).
 *
 * Randall, 21 Oct 2026: fitters get a pack's email both ways, to send themselves and to have H-G send it. It goes
 * only to golfers who opted in (the fitter ticks that every golfer they add agreed), in the fitter's name from H-G's
 * address, with H-G's address and two ways out (22 Oct: "Stop emails from <fitter>", which keeps the golfer on H-G's
 * own emails, and MailerLite's unsubscribe from all), and unsubscribes are kept for good. Each send needs his OK:
 * he looks at the email exactly as it would go, and the OK is keyed to it. If anything in it moves before the send,
 * the OK no longer holds and the send comes back here.
 */

const STATUS = {
  new: ['Waiting for your OK', 'bg-amber-50 text-amber-800 border-amber-300'],
  approved: ['Sending', 'bg-hg-green/10 text-hg-green border-hg-green/30'],
  sending: ['Sending', 'bg-hg-green/10 text-hg-green border-hg-green/30'],
  scheduled: ['With MailerLite', 'bg-hg-green/10 text-hg-green border-hg-green/30'],
  sent: ['Sent', 'bg-hg-green/10 text-hg-green border-hg-green/30'],
  declined: ['You said no', 'bg-hg-surface-hover text-hg-text-dim border-hg-border'],
  nothing_to_send: ['Nobody could receive it', 'bg-hg-surface-hover text-hg-text-dim border-hg-border'],
  failed: ['Stopped', 'bg-red-50 text-red-700 border-red-300'],
};

function Chip({ status }) {
  const [words, cls] = STATUS[status] || [status, 'bg-hg-surface-hover text-hg-text-dim border-hg-border'];
  return <span className={`text-[10px] font-sans uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${cls}`}>{words}</span>;
}

const day = (iso) => (iso ? String(iso).slice(0, 10) : '');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function ListLine({ list }) {
  if (!list) return null;
  const extra = [];
  if (list.unsubscribed) extra.push(`${list.unsubscribed} unsubscribed`);
  if (list.cannot_receive) extra.push(`${list.cannot_receive} can't receive email`);
  if (list.opted_out) extra.push(`${list.opted_out} opted out of H-G email`);
  return (
    <span>
      {plural(list.on_list, 'golfer', 'golfers')} on the list
      {list.unchecked ? ` (${list.unchecked} not yet checked with MailerLite, which happens when it sends)` : ''}
      {extra.length ? `; ${extra.join(', ')}` : ''}
    </span>
  );
}

function SendView({ id, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [reason, setReason] = useState('');
  const [declining, setDeclining] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/fitter-sends/${id}`)
      .then((res) => { setData(res.data); setError(''); })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not read this send.'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = (what, url, body) => {
    setBusy(what);
    setMsg('');
    axios.post(url, body)
      .then(() => { load(); onChanged(); })
      .catch((e) => setMsg(e?.response?.data?.detail || 'That did not go through.'))
      .finally(() => setBusy(''));
  };

  if (error) return <div className="text-sm font-sans text-red-700">{error}</div>;
  if (!data) return <Loader2 className="animate-spin text-hg-text-dim" size={18} />;
  const { send, email, problems } = data;
  const fitter = send.fitter || {};
  return (
    <div className="space-y-4" data-testid="fitter-send">
      <button onClick={onBack} className="text-xs font-sans text-hg-text-dim inline-flex items-center gap-1 hover:text-hg-text">
        <ArrowLeft size={12} /> All sends
      </button>
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="text-lg font-display text-hg-text">{send.label} — for {fitter.name}</h2>
        <Chip status={send.status} />
      </div>
      <div className="text-sm font-sans text-hg-text space-y-1">
        <div><span className="text-hg-text-dim">To:</span> <ListLine list={send.list} /></div>
        {email && <div><span className="text-hg-text-dim">From:</span> {email.from_name} &lt;{email.from_email}&gt;</div>}
        {email && <div><span className="text-hg-text-dim">Subject:</span> {email.subject}</div>}
        <div className="text-xs text-hg-text-dim">
          Asked {day(send.requested_at || send.received_at)}. Replies come to {email ? email.from_email : 'the sending address'}.
          Each email ends with two ways out: Stop emails from {fitter.name}, which keeps the golfer on Henry-Griffitts' own
          emails, and Unsubscribe from all Henry-Griffitts email.
        </div>
        {email && email.pack_changed && (
          <div className="text-xs text-amber-700">The pack has changed since the fitter asked. This is the pack as it stands now.</div>
        )}
        {send.void_reason && send.status === 'new' && (
          <div className="text-xs text-amber-700">Your earlier OK no longer holds: {send.void_reason}</div>
        )}
      </div>

      {problems.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2 text-sm font-sans text-amber-800" data-testid="send-problems">
          {problems.map((p) => <div key={p} className="flex items-start gap-2"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {p}</div>)}
        </div>
      )}

      {send.status === 'new' && (
        <div className="flex items-center gap-2 flex-wrap">
          <button disabled={!email || !!busy} data-testid="approve-send"
            onClick={() => act('approve', `${API}/fitter-sends/${id}/approve`, { hash: email.hash })}
            className="inline-flex items-center gap-1.5 bg-hg-green text-white text-sm font-sans px-3 py-1.5 rounded-sm disabled:opacity-40">
            {busy === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send it to {fitter.name}'s golfers
          </button>
          {!declining && (
            <button onClick={() => setDeclining(true)} className="inline-flex items-center gap-1 text-sm font-sans text-hg-text-dim border border-hg-border px-3 py-1.5 rounded-sm">
              <X size={14} /> Say no
            </button>
          )}
          {declining && (
            <span className="inline-flex items-center gap-2 flex-wrap">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why, for the record (the fitter is not shown it)"
                className="border border-hg-border rounded-sm px-2 py-1 text-sm font-sans w-80 max-w-full" data-testid="decline-reason" />
              <button disabled={!reason.trim() || !!busy} onClick={() => act('decline', `${API}/fitter-sends/${id}/decline`, { reason })}
                className="text-sm font-sans text-red-700 border border-red-300 px-3 py-1.5 rounded-sm disabled:opacity-40" data-testid="decline-send">
                Say no
              </button>
            </span>
          )}
        </div>
      )}
      {send.status === 'failed' && (
        <div className="space-y-2">
          <div className="text-sm font-sans text-red-700" data-testid="send-error">{send.error}</div>
          <button disabled={!!busy} onClick={() => act('retry', `${API}/fitter-sends/${id}/retry`)}
            className="inline-flex items-center gap-1.5 text-sm font-sans border border-hg-border px-3 py-1.5 rounded-sm disabled:opacity-40">
            <RotateCcw size={14} /> Try again
          </button>
        </div>
      )}
      {['scheduled', 'sent', 'nothing_to_send'].includes(send.status) && (
        <div className="text-sm font-sans text-hg-text-dim">
          {send.status === 'sent' && `MailerLite sent it to ${plural(send.delivered || 0, 'golfer', 'golfers')} on ${day(send.sent_at)}.`}
          {send.status === 'scheduled' && `MailerLite was told to send it to ${plural(send.recipients || 0, 'golfer', 'golfers')}. It shows as sent once MailerLite says it went.`}
          {send.status === 'nothing_to_send' && 'None of the fitter\'s golfers could get email, so nothing was sent.'}
          {send.campaign_id ? ` Campaign ${send.campaign_id}.` : ''}
        </div>
      )}
      {send.status === 'declined' && <div className="text-sm font-sans text-hg-text-dim">You said no: {send.reason}</div>}
      {msg && <div className="text-sm font-sans text-red-700">{msg}</div>}

      {email && (
        <section>
          <div className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim mb-1">The email, exactly as the golfers get it</div>
          <iframe title="Email as sent" srcDoc={email.html} sandbox="" className="w-full h-[1500px] border border-hg-border rounded-sm bg-white" />
        </section>
      )}
    </div>
  );
}

export default function FitterSends() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/fitter-sends`)
      .then((res) => {
        if (!Array.isArray(res?.data?.sends)) throw new Error('bad shape');
        setData(res.data);
        setError('');
      })
      .catch(() => setError('Could not read the emails fitters asked H-G to send.'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="text-xs font-sans text-red-700" data-testid="sends-error">{error}</div>;
  if (!data || data.sends.length === 0) return null;
  if (open) return <SendView id={open} onBack={() => { setOpen(null); load(); }} onChanged={load} />;

  const waiting = data.sends.filter((s) => s.status === 'new' || s.status === 'failed');
  const rest = data.sends.filter((s) => s.status !== 'new' && s.status !== 'failed');
  const row = (s) => (
    <button key={s.id} onClick={() => setOpen(s.id)} data-testid={`send-${s.id}`}
      className="w-full text-left bg-white border border-hg-border rounded-sm px-4 py-3 hover:bg-hg-surface-hover flex items-center gap-3 flex-wrap">
      <span className="text-sm font-sans text-hg-text font-medium">{s.fitter?.name}</span>
      <span className="text-sm font-sans text-hg-text-dim">{s.label}</span>
      <Chip status={s.status} />
      <span className="ml-auto text-[11px] font-sans text-hg-text-dim"><ListLine list={s.list} /></span>
    </button>
  );
  return (
    <section className="space-y-2" data-testid="fitter-sends">
      <div className="text-sm font-sans font-medium text-hg-text inline-flex items-center gap-2"><Send size={14} className="text-hg-green" /> Emails fitters asked H-G to send</div>
      <p className="text-xs font-sans text-hg-text-dim max-w-3xl">
        A fitter's pack email to the golfers on their list, in the fitter's name from {data.from_email}. Nothing goes until you look at it and press Send.
      </p>
      {waiting.map(row)}
      {rest.length > 0 && <div className="text-[11px] font-sans uppercase tracking-wider text-hg-text-dim pt-2">Earlier</div>}
      {rest.map(row)}
    </section>
  );
}
